import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertTriangle, Info, X, AlertCircle } from 'lucide-react';

const ICONS = {
  success: <CheckCircle className="h-4 w-4" />,
  error: <AlertCircle className="h-4 w-4" />,
  warning: <AlertTriangle className="h-4 w-4" />,
  info: <Info className="h-4 w-4" />,
};

const ICON_BG = {
  success: 'bg-green-500/10 text-green-500',
  error: 'bg-red-500/10 text-red-500',
  warning: 'bg-yellow-400/10 text-yellow-500',
  info: 'bg-blue-500/10 text-blue-500',
};

export default function Banner({
  type = 'info',
  title = '',
  message = '',
  onClose,
  autoHide = true,
  autoHideDelay = 5000,
  position = 'top-right', // 'top-right', 'top-center', etc.
  className = '',
  action = null, // { label: 'Button Text', onClick: () => {} }
}) {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (autoHide && autoHideDelay > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, autoHideDelay);
      return () => clearTimeout(timer);
    }
  }, [autoHide, autoHideDelay]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose?.();
    }, 500);
  };

  if (!isVisible) return null;

  const getPositionClasses = () => {
    switch (position) {
      case 'top-center':
        return 'top-6 left-1/2 transform -translate-x-1/2';
      case 'top-left':
        return 'top-6 left-6';
      case 'bottom-right':
        return 'bottom-6 right-6';
      case 'bottom-center':
        return 'bottom-6 left-1/2 transform -translate-x-1/2';
      case 'bottom-left':
        return 'bottom-6 left-6';
      case 'top-right':
      default:
        return 'top-6 right-6';
    }
  };

  return (
    <div className={`fixed ${getPositionClasses()} z-[10001] transform transition-all duration-500 ease-out ${isVisible ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0'} ${className}`}>
      <div
        className="backdrop-blur-sm border border-gray-400 dark:border-border/50 rounded-lg shadow-xl p-4 max-w-sm bg-[rgba(255,255,255,0.92)] dark:bg-[rgba(24,26,32,0.92)]"
      >
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5">
            <div className={`size-8 rounded-full flex items-center justify-center ${ICON_BG[type]}`}>{ICONS[type]}</div>
          </div>
          <div className="flex-1 min-w-0">
            {title && (
              <h3 className="text-sm font-medium text-foreground mb-1 dark:text-white">{title}</h3>
            )}
            <p className="text-xs text-gray-700 dark:text-gray-200">{message}</p>
            {action && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  action.onClick();
                  handleClose();
                }}
                className="mt-2 px-3 py-1.5 text-xs font-medium rounded-md bg-primary text-white hover:bg-primary/90 transition-colors"
              >
                {action.label}
              </button>
            )}
          </div>
          <button
            onClick={handleClose}
            className="flex-shrink-0 ml-2 text-gray-600 dark:text-gray-300 hover:text-foreground transition-colors"
            title="Close notification"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
} 